import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react'
import { DanceClass, Schedule } from '../core'
import { useScheduleData } from '../hooks'
import { 
  parseISTDate, 
  getTodayIST, 
  formatISTDate, 
  getDisplayDateIST,
  addDaysIST,
  isScheduleInFuture
} from '../core'

interface InlineDateCalendarProps {
  danceClass: DanceClass | null
  selectedDate: string
  onDateSelect: (date: string) => void
  isOpen: boolean
  onToggle: () => void
  isDemo?: boolean
}

export default function InlineDateCalendar({ 
  danceClass, 
  selectedDate, 
  onDateSelect, 
  isOpen, 
  onToggle,
  isDemo = false
}: InlineDateCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  
  // Use custom hook for schedule data
  const { 
    allClassSchedules, 
    isLoading,
    error
  } = useScheduleData({ 
    danceClass, 
    isDemo 
  })


  // Generate available dates for the next 3 months
  const generateAvailableDates = () => {
    if (!danceClass || allClassSchedules.length === 0) return new Map()
    
    const dates = new Map<string, Schedule[]>()
    const today = getTodayIST()
    const endDate = addDaysIST(formatISTDate(today), 90) // 3 months ahead

    // Generate dates for the next 3 months
    let currentDate = today
    const endDateParsed = parseISTDate(endDate)
    while (currentDate <= endDateParsed) {
      const dateString = formatISTDate(currentDate)
      
      // Get ALL future schedules for this date (including those within 1 hour)
      const daySchedules = allClassSchedules.filter(schedule => 
        schedule.specificDate === dateString && 
        isScheduleInFuture(schedule.specificDate, schedule.startTime)
      )

      // Show the date if it has any future schedules (even if some are within 1 hour)
      if (daySchedules.length > 0) {
        dates.set(dateString, daySchedules)
      }
      
      currentDate = parseISTDate(addDaysIST(dateString, 1))
    }

    return dates
  }

  // Generate available schedules when data changes
  const availableSchedules = generateAvailableDates()

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days = []
    const today = getTodayIST()

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      const dateString = formatISTDate(date) // Use IST formatting
      const isCurrentMonth = date.getMonth() === month
      const isToday = date.getTime() === today.getTime()
      
      // Check if date is past (considering time for today)
      let isPast = date < today
      if (isToday) {
        // For today, check if there are any future schedules (including those within 1 hour)
        const todaySchedules = allClassSchedules.filter(schedule => 
          schedule.specificDate === dateString && 
          isScheduleInFuture(schedule.specificDate, schedule.startTime)
        )
        isPast = todaySchedules.length === 0
      }
      
      const hasSchedules = availableSchedules.has(dateString)
      const isSelected = selectedDate === dateString

      days.push({
        date,
        dateString,
        isCurrentMonth,
        isToday,
        isPast,
        hasSchedules,
        isSelected,
        schedules: availableSchedules.get(dateString) || []
      })
    }

    return days
  }

  const calendarDays = generateCalendarDays()
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const handleDateClick = (day: any) => {
    if (day.hasSchedules && !day.isPast) {
      onDateSelect(day.dateString)
      onToggle() // Close calendar after selection
    }
  }

  const formatSelectedDate = (dateString: string) => {
    if (!dateString) return ''
    return getDisplayDateIST(dateString)
  }

  if (!danceClass) return null

  return (
    <div className="relative">
      {/* Date Input Button */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left flex items-center justify-between"
        disabled={isLoading}
      >
        <span className={selectedDate ? 'text-gray-900' : 'text-gray-500'}>
          {isLoading ? 'Loading dates...' : selectedDate ? formatSelectedDate(selectedDate) : 'Select a date'}
        </span>
        <CalendarIcon className="w-4 h-4 text-gray-400" />
      </button>

      {/* Calendar Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4"
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">Select Date</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium min-w-[100px] text-center">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} className="p-1 text-center text-xs font-medium text-gray-500">
                  {day}
                </div>
              ))}

              {/* Loading state */}
              {isLoading ? (
                <div className="col-span-7 flex items-center justify-center py-8">
                  <div className="text-sm text-gray-500">Loading available dates...</div>
                </div>
              ) : error ? (
                <div className="col-span-7 flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="text-sm text-red-600 mb-2">Service temporarily unavailable</div>
                    <div className="text-xs text-gray-500">Please try again after some time</div>
                  </div>
                </div>
              ) : (
                /* Calendar days */
                calendarDays.map((day, index) => (
                <motion.button
                  key={index}
                  type="button"
                  onClick={() => handleDateClick(day)}
                  className={`
                    relative p-2 h-8 rounded text-xs transition-all duration-200
                    ${!day.isCurrentMonth ? 'text-gray-300' : ''}
                    ${day.isPast ? 'text-gray-400 cursor-not-allowed' : ''}
                    ${day.hasSchedules && !day.isPast ? 'cursor-pointer hover:bg-primary-50' : ''}
                    ${day.isSelected ? 'bg-primary-500 text-white' : ''}
                    ${day.isToday && !day.isSelected ? 'bg-blue-100 text-blue-700 font-semibold' : ''}
                  `}
                  disabled={day.isPast || !day.hasSchedules}
                  whileHover={day.hasSchedules && !day.isPast ? { scale: 1.05 } : {}}
                  whileTap={day.hasSchedules && !day.isPast ? { scale: 0.95 } : {}}
                >
                  <div className="flex flex-col items-center">
                    <span className="font-medium">{day.date.getDate()}</span>
                    {day.hasSchedules && !day.isPast && (
                      <div className="flex gap-0.5 mt-0.5">
                        {day.schedules.slice(0, 2).map((schedule: Schedule, i: number) => (
                          <div
                            key={i}
                            className={`w-1 h-1 rounded-full ${
                              day.isSelected ? 'bg-white' : 
                              schedule.isFullyBooked ? 'bg-red-500' : 'bg-primary-500'
                            }`}
                            title={schedule.isFullyBooked ? 'Fully Booked' : `${schedule.remainingSpots || 0} spots left`}
                          />
                        ))}
                        {day.schedules.length > 2 && (
                          <div className={`text-xs ${day.isSelected ? 'text-white' : 'text-primary-600'}`}>
                            +{day.schedules.length - 2}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.button>
                ))
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 pt-3 border-t">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-100"></div>
                <span>Today</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                <span>Unavailable</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
